// The quote flow's server calls: the view and selection endpoints, plus a
// FormV2 client that re-presents the quote credential for the application
// form. All of it speaks to after-api's /quotes/{quoteId} gateway. The
// credential itself never rides these calls — it lives in an HttpOnly
// `quote_session` cookie the gateway sets from `/session`, and every
// request here relies on `credentials: "include"` (fetchPublicAPI's
// default) to carry it. `mintQuoteSession` is the one call that presents a
// credential of its own (the link token, or the signed handoff), because
// minting the cookie is the whole point of that call.

import { api, fetchPublicAPI } from "hooks/usePublicAPI";
import type { QuoteType } from "./analytics";
import type { QuotePackage } from "components/AtNeedQuote/helpers";
import type { FormV2Client } from "components/FormV2/api";
import type {
  V2ResolvedView,
  V2ResumeResponse,
  V2SavedAnswer,
} from "components/FormV2/types";

export interface QuotePaymentOption {
  /** The option's stable name (`single_pay`, `5_year`,
   * `5_year_installments`, …) — what selection posts and flag payloads
   * list. */
  slug: string;
  payment_period: string;
  payment_period_oap: string;
  display_name: string;
  /** Who writes the policy for this option — logo assets key on the
   * stable slug, the name is display text. */
  carrier_id: string;
  carrier_name: string;
  due_today_cents: number;
  recurring_cents: number | null;
  total_cents: number;
  term_months: number | null;
}

export interface QuotePlan {
  slug: string;
  name: string;
  features: { label: string; type: string | null }[];
  payment_options: QuotePaymentOption[];
}

export interface QuoteSelection {
  id: string;
  plan_slug: string;
  payment_period: string;
  payment_period_oap: string;
  source: string;
  /** The amounts frozen when this selection was recorded — the old side
   * of any confirm-updated-quote diff. Null on a rep seed (nothing was
   * shown, so nothing froze). */
  due_today_cents: number | null;
  recurring_cents: number | null;
  total_cents: number | null;
}

/** An agreement act recorded against a selection — a newer selection
 * orphans the act and its screen simply reappears. */
export interface QuoteAcknowledgement {
  kind: string;
  selection_id: string;
  outcome: string | null;
  created_at: string;
}

export interface QuoteViewResponse {
  /** Names this payload as the policy view; absent from a server that
   * predates the union. */
  kind?: "policy";
  quote: {
    id: string;
    subject_type: QuoteType;
    status: string;
    /** Which channel created this quote — a family's web lead, a person in
     * After admin, or the AI workflow service. Passed through to analytics
     * as it arrives, never narrowed, so a channel added server-side needs
     * no client deploy to be reportable. */
    origin_kind: string;
    created_at: string;
  };
  /** The door this visitor came in through — the channel of the link their
   * session was redeemed from. Null when no link was involved: a lead-form
   * signature mints a session directly. Optional because a server that
   * predates the field answers nothing at all, which is not the same as
   * answering that there was no link. */
  grant_channel?: string | null;
  /** Who asked for the credential this visitor holds. Distinct from the
   * quote's own origin, since the AI workflow can mint a link into a quote
   * a family's web lead created. Null for a grant minted before grants
   * recorded an actor; optional for the same reason as the channel. */
  grant_origin?: string | null;
  step: string;
  /** The station the quote opened on — `"quiz"` for a lead-submission
   * quote, null for every other creation path (a rep seed, an AI-minted
   * quote, and every quote that predates the quiz). Fixed at creation;
   * never changes for the life of the quote. */
  opening_step: "quiz" | null;
  /** The append-only checkpoint ledger's CURRENT rows only — the quiz's
   * standing conclusion and the call-gate's standing outcome, when either
   * exists. A superseded or withdrawn row never appears here; the staff
   * record view is the one that carries the full history. */
  checkpoints: QuoteCheckpoint[];
  /** Whose plan this is — the greeting addresses the owner, and when they
   * plan for a loved one the insured is named separately. */
  planning_for: "self" | "loved_one";
  insured_first_name: string | null;
  insured_full_name: string | null;
  /** For the "Quote prepared … for {name}, {age}, ZIP {zip}" line. */
  insured_age: number | null;
  insured_zip: string | null;
  /** The policy's market slug ("AZ1") — localizes the review. */
  market_slug: string | null;
  owner_first_name: string | null;
  /** The number a booked call reaches — the owner's phone. */
  contact_phone: string | null;
  application_form_ref: string;
  /** The last completed evaluation said no and nothing has moved since —
   * a reload lands the application on the options face, not the walk. */
  ineligible: boolean;
  current_selection: QuoteSelection | null;
  acknowledgements: QuoteAcknowledgement[];
  pricing: QuotePricing;
}

/** One at-need package as the case view offers it: a single payment, with the
 * engine's own quote as the `snapshot` the price breakdown renders from. */
export interface CaseQuotePaymentOption {
  slug: string;
  display_name: string;
  total_cents: number;
  snapshot: QuotePackage;
}

export interface CaseQuotePlan {
  slug: string;
  name: string;
  payment_options: CaseQuotePaymentOption[];
}

/** The view of a quote for a case (an at-need or imminent lead): the same
 * `quote` envelope and `{status, plans}` pricing as the policy view, priced
 * live from the case, and the facts the at-need quote display words itself
 * from. */
export interface CaseQuoteViewResponse {
  kind: "case";
  quote: QuoteViewResponse["quote"];
  grant_channel?: string | null;
  grant_origin?: string | null;
  /** `quote`, `arrangement` once a package is chosen, `complete` once the
   * case reached Dispatch, `closed` otherwise. */
  step: string;
  current_selection: QuoteSelection | null;
  pricing: {
    status: QuotePricing["status"];
    plans: CaseQuotePlan[];
  };
  case_id: string;
  /** A death already reported, or a death expected. */
  case_variant: "at_need" | "imminent";
  /** The family said the loved one pre-planned with After. */
  preplanned: boolean;
  decedent_first_name: string | null;
  customer_first_name: string | null;
  /** The pickup address the add-on lookups price against. */
  dcd_location: string | null;
  /** The package the display leads with: Base in a Base+ market. */
  selected_package: string | null;
  is_estimate: boolean;
  is_v2_market: boolean;
}

/** The quote screen's inputs before the quote exists — everything the view
 * carries, priced live off the signed lead payload instead of a stored
 * quote. The client renders it through the exact same quote-screen path as
 * a real view; that's the point of sharing the shape. */
export type QuoteBootstrapResponse = Omit<QuoteViewResponse, "quote"> & {
  quote: null;
  /** When these prices were computed — the "Quote prepared …" line. */
  prepared_at: string;
  /** The station the flow opens on, as far as the server can say before
   * a session exists — capped at `quote`, so this read discloses only
   * whether the intake gate is behind the family. Optional because a
   * server that predates it answers nothing, and nothing is not a
   * landing the flow may act on. */
  landing?: "quiz" | "schedule_call" | "quote";
};

/** How a rep's settings shaped the offer. Absent means not customized. */
export interface QuoteOfferScope {
  /** True when the rep's settings shaped these plans; false when no settings
   * exist or none of the chosen options survived (the default offer). */
  customized: boolean;
  /** Base's features when the settings hid Base, so the "What all plans
   * include" panel keeps showing; null otherwise. */
  baseline_features: QuotePlan["features"] | null;
}

export interface QuotePricing {
  status: "ok" | "out_of_area" | "unavailable";
  plans: QuotePlan[];
  offer_scope?: QuoteOfferScope;
}

/** One decline cause, as a slug to match on — never a rendered
 * sentence. `refs` rides only on carrier_answers: the failing question
 * refs, as data. */
export interface DeclineReason {
  kind: "out_of_area" | "age" | "carrier_answers" | "terminal";
  refs?: string[];
}

/** The offer priced over the application's current answers, and R18's
 * verdict: can_issue is false exactly when the evaluation ran and offered
 * no options; one that could not run fails open. Reasons are the distinct
 * slugged causes when the verdict is no — empty otherwise. */
export interface QuoteEvaluation {
  can_issue: boolean;
  reasons: DeclineReason[];
  pricing: QuotePricing;
}

/** One current row of the quote's checkpoint ledger — a kind's standing
 * resolution, as the view carries it. `resolution` is opaque here (the
 * registered kinds and their shapes live server-side); callers narrow it
 * for the kind they read. */
export interface QuoteCheckpoint {
  kind: string;
  resolution: unknown;
  created_at: string;
}

export interface AcknowledgedSelection {
  selection: QuoteSelection;
  acknowledgement: QuoteAcknowledgement;
  step: string;
}

export function fetchQuoteView(quoteId: string): Promise<QuoteViewResponse> {
  return fetchPublicAPI<QuoteViewResponse>(`/quotes/${quoteId}/view`);
}

/** The case quote's own read of the same endpoint. */
export function fetchCaseQuoteView(
  quoteId: string,
): Promise<CaseQuoteViewResponse> {
  return fetchPublicAPI<CaseQuoteViewResponse>(`/quotes/${quoteId}/view`);
}

/** The pre-arrival read: view-shaped cards priced off the signed handoff
 * payload, ahead of any credential — verified upstream by HMAC alone. */
export function fetchQuoteBootstrap(
  quoteId: string,
  handoff: { ped: string; sig: string; exp: string },
): Promise<QuoteBootstrapResponse> {
  const params = new URLSearchParams(handoff);
  return fetchPublicAPI<QuoteBootstrapResponse>(
    `/quotes/${quoteId}/bootstrap?${params.toString()}`,
  );
}

/** What opens a device session: a minted link's token, or the signed
 * handoff a bootstrap render is already showing cards from. */
export type QuoteCredential =
  | { kind: "link"; token: string }
  | { kind: "signature"; ped: string; sig: string; exp: string };

/** The quote's presence — whether the id names a quote, and whether it is
 * still sitting at the quote screen. What a refused mint or a viewless
 * bare arrival reads to choose between the priced cards and asking the
 * family to call. */
export interface QuotePresence {
  claimed: boolean;
  at_quote_screen: boolean;
  /** What the quote is for. Absent from a server that predates it, which
   * reads as a policy. */
  subject_type?: QuoteType;
}

/** The 409 body a refused mint carries — a real credential that can no
 * longer open a session (redeemed, expired, revoked, unbound). */
export interface SessionRefusal {
  error: string;
  message: string;
  next_steps: string[];
  presence: QuotePresence | null;
}

/** The one mint: a link token or a signed handoff becomes the device
 * session — an HttpOnly cookie the gateway sets, never a token this
 * client holds. A grant may carry a `redirect` to another quote the same
 * lead owns. Refuses with a NetworkError whose responseData is a
 * SessionRefusal on 409, blank on 404. */
export function mintQuoteSession(
  quoteId: string,
  credential: QuoteCredential,
): Promise<{ granted: true; redirect?: string }> {
  return fetchPublicAPI<{ granted: true; redirect?: string }>(
    `/quotes/${quoteId}/session`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential }),
    },
  );
}

/** Whether the quote is already claimed and, if so, whether that claim is
 * sitting at the quote screen. */
export function fetchQuotePresence(quoteId: string): Promise<QuotePresence> {
  return fetchPublicAPI<QuotePresence>(`/quotes/${quoteId}/presence`);
}

/** Asks for a sign-in grant for the account the lead form began: no body,
 * and no destination, because the server only ever uses the channels typed
 * at submission. A blank 404 means this quote cannot create an account. */
export function requestAccountGrant(quoteId: string): Promise<unknown> {
  return fetchPublicAPI<unknown>(`/quotes/${quoteId}/account-grant`, {
    method: "POST",
  });
}

/** Exchanges a signed-in visitor's own session for the quote's device
 * session, when they are a contact on the quote. Sends the bearer token,
 * so it goes through the authenticated client. */
export function requestSignedInSession(
  quoteId: string,
  getIdToken: () => Promise<string | null> | undefined,
): Promise<{ granted: true }> {
  return api(
    { method: "POST" },
    getIdToken,
  )(`/quotes/${quoteId}/signed-in-session`) as Promise<{ granted: true }>;
}

export function recordSelection(
  quoteId: string,
  selection: {
    plan_slug: string;
    option_slug: string;
    /** The juncture's pick face passes make_selection — its choice is
     * priced over the walk's answers and settles the carrier variant. */
    source?: "make_selection";
  },
): Promise<{ selection: QuoteSelection; step: string }> {
  return fetchPublicAPI<{ selection: QuoteSelection; step: string }>(
    `/quotes/${quoteId}/selection`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(selection),
    },
  );
}

/** Writes (or overwrites) a checkpoint kind's current resolution — a
 * no-op server-side when it deep-equals the row already standing.
 * `resolution`'s shape is whatever that kind's registry entry requires
 * (after-internal validates it); this call never inspects it. */
export function putCheckpoint(
  quoteId: string,
  kind: string,
  resolution: unknown,
  options: { keepalive?: boolean } = {},
): Promise<{ checkpoint: QuoteCheckpoint; step: string }> {
  return fetchPublicAPI<{ checkpoint: QuoteCheckpoint; step: string }>(
    `/quotes/${quoteId}/checkpoints/${kind}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(resolution),
      ...options,
    },
  );
}

/** Withdraws a checkpoint kind's current resolution — idempotent; nothing
 * current to withdraw still answers success. */
export function deleteCheckpoint(
  quoteId: string,
  kind: string,
): Promise<{ step: string }> {
  return fetchPublicAPI<{ step: string }>(
    `/quotes/${quoteId}/checkpoints/${kind}`,
    { method: "DELETE" },
  );
}

/** The Edit-application act: reopens the completed walk for editing — a
 * fresh in-progress copy of the response, the completed one retired
 * behind it. Every answer carries over; the signed packet goes stale and
 * the flow re-signs after re-completion. Idempotent on the double-tap
 * (`created: false` when a reopened walk already stood). */
export function editApplication(
  quoteId: string,
): Promise<{ response_id: string; created: boolean; step: string }> {
  return fetchPublicAPI<{
    response_id: string;
    created: boolean;
    step: string;
  }>(`/quotes/${quoteId}/application/edit`, { method: "POST" });
}

/** The payment step's capture body. Card is the only instrument today;
 * the discriminant is what lets ACH join additively. */
export interface CardPaymentMethodInput {
  type: "card";
  cc_full_name: string;
  cc_number: string;
  cc_exp: string;
  cc_cvc: string;
  cc_billing_address_zip: string;
}

/** How a card said no, in the family's words: the server keys the
 * sentence off the decline code and says whether the same card is worth
 * another try. The processor's own words never reach the browser. */
export interface FamilyDecline {
  code: string | null;
  message: string;
  allow_retry: boolean;
}

/** Where a stored card stands as a chargeable instrument. Continue waits
 * on `ready`; `pending` polls; `failed` reveals the decline; `unminted`
 * is a card nothing has verified yet (verify it first). */
export type PaymentInstrumentState =
  "unminted" | "pending" | "ready" | "failed";

export interface PaymentInstrumentSummary {
  /** A string on purpose upstream — unknown values read as not ready. */
  state: PaymentInstrumentState | string;
  failure: FamilyDecline | null;
}

/** A stored card as the flow sees it — metadata only, never the
 * instrument. Brand is null on records that predate stamping. */
export interface PaymentMethodSummary {
  policy_payment_id: string;
  brand: string | null;
  last_4: string | null;
  exp: string | null;
  instrument: PaymentInstrumentSummary;
}

export interface PaymentMethodsView {
  methods: PaymentMethodSummary[];
  /** Null = no restriction applies (single-pay, or an unconfigured
   * carrier) — skip brand gating entirely. */
  accepted_credit_cards: string[] | null;
  carrier_name: string | null;
}

export function fetchPaymentMethods(
  quoteId: string,
): Promise<PaymentMethodsView> {
  return fetchPublicAPI<PaymentMethodsView>(
    `/quotes/${quoteId}/payment-methods`,
  );
}

/** Adds a card to the policy — no artifact is written; the flow advances
 * only through confirmPaymentMethod. `duplicate` means the fingerprint
 * matched a stored card and `method` IS that card. */
export function addPaymentMethod(
  quoteId: string,
  card: CardPaymentMethodInput,
): Promise<{ method: PaymentMethodSummary; duplicate: boolean }> {
  return fetchPublicAPI<{ method: PaymentMethodSummary; duplicate: boolean }>(
    `/quotes/${quoteId}/payment-methods`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(card),
    },
  );
}

/** Starts (or restarts) verifying a stored card as a chargeable
 * instrument. Answers with the card as it now stands — pending when a
 * verification was just started; poll the list for the outcome. */
export function verifyPaymentMethod(
  quoteId: string,
  policyPaymentId: string,
): Promise<{ method: PaymentMethodSummary }> {
  return fetchPublicAPI<{ method: PaymentMethodSummary }>(
    `/quotes/${quoteId}/payment-methods/${policyPaymentId}/verification`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
}

/** The payment step's explicit forward act — nothing else advances it.
 * Refuses (409) a card that is not verified yet. */
export function confirmPaymentMethod(
  quoteId: string,
  policyPaymentId: string,
): Promise<{ step: string }> {
  return fetchPublicAPI<{ step: string }>(`/quotes/${quoteId}/payment-method`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ policy_payment_id: policyPaymentId }),
  });
}

/** Removes a stored card; a confirmation pointing at it voids with it.
 * Returns the fresh list so the screen re-renders from truth. */
export function deletePaymentMethod(
  quoteId: string,
  policyPaymentId: string,
): Promise<PaymentMethodsView> {
  return fetchPublicAPI<PaymentMethodsView>(
    `/quotes/${quoteId}/payment-methods/${policyPaymentId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// The sign step
// ---------------------------------------------------------------------------

export interface SignSigner {
  role: "owner" | "insured";
  name: string | null;
  has_completed: boolean;
}

export type SignDocumentState =
  "none" | "generating" | "ready" | "family_signed" | "stale" | "error";

export interface SignView {
  carrier: {
    name: string;
    requires_insured_signature: boolean;
    requires_separate_document_packet: boolean;
  } | null;
  signers: SignSigner[];
  document: {
    state: SignDocumentState;
    detail: string | null;
  };
  attestation_text: string;
}

export interface SignSession {
  session_id: string;
  expires_at: string | null;
  signer_role: "owner" | "insured";
}

/** The sign screen's read — safe to poll while preparing and verifying. */
export function fetchSignView(quoteId: string): Promise<SignView> {
  return fetchPublicAPI<SignView>(`/quotes/${quoteId}/sign`);
}

/** Drives the document packet to signable — idempotent and slow (build
 * and send are real work). Fire it and poll the view; never block a
 * screen on this response. */
export function ensureSignDocuments(quoteId: string): Promise<SignView> {
  return fetchPublicAPI<SignView>(`/quotes/${quoteId}/sign/documents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

export interface FinalizeView {
  /** The quote's policy; null when the quote has none. */
  policy_id: string | null;
  /** The frozen agreement the screen recaps — null fields on a rep seed. */
  amount: {
    due_today_cents: number | null;
    recurring_cents: number | null;
    total_cents: number | null;
    plan_slug: string | null;
    payment_period: string | null;
  };
  payment_method: {
    brand: string | null;
    last_4: string | null;
    exp: string | null;
  } | null;
  carrier: {
    name: string;
    requires_separate_document_packet: boolean;
  } | null;
  charge: {
    /** none | processing | succeeded — a string on purpose; new states
     * must not break this client. */
    state: string;
    amount_cents: number | null;
  };
}

/** The finalize screen's read — safe to poll while a charge settles. */
export function fetchFinalizeView(quoteId: string): Promise<FinalizeView> {
  return fetchPublicAPI<FinalizeView>(`/quotes/${quoteId}/finalize`);
}

/** The body of a 402 from the finalize submit: the family's decline
 * copy and whether the same card is worth another try. */
export interface ChargeDeclinedResponse {
  error: "quote_charge_declined";
  message: string;
  decline_code: string | null;
  allow_retry: boolean;
}

/** Pay and submit — charges exactly the agreed amount (or records the
 * nothing-due submit). Idempotent server-side; a decline throws a
 * NetworkError (402) whose responseData is a ChargeDeclinedResponse. */
export function submitFinalize(quoteId: string): Promise<FinalizeView> {
  return fetchPublicAPI<FinalizeView>(`/quotes/${quoteId}/finalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/** An embedded signing session for one family signer. The insured's
 * requires `attested: true` — the interstitial's confirm — on every
 * mint, reloads included. */
export function createSignSession(
  quoteId: string,
  body: { signer_role: "owner" | "insured"; attested?: boolean },
): Promise<SignSession> {
  return fetchPublicAPI<SignSession>(`/quotes/${quoteId}/sign/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** The end-of-form juncture's read — also the checkpoints' verdict. */
export function evaluateQuote(quoteId: string): Promise<QuoteEvaluation> {
  return fetchPublicAPI<QuoteEvaluation>(`/quotes/${quoteId}/evaluate`);
}

/** Confirm-updated-quote: the named option at the freshly shown amounts,
 * frozen upstream with the act recorded. */
export function confirmChange(
  quoteId: string,
  selection: { plan_slug: string; option_slug: string },
): Promise<AcknowledgedSelection> {
  return fetchPublicAPI<AcknowledgedSelection>(
    `/quotes/${quoteId}/change-confirmation`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(selection),
    },
  );
}

export function decideUpsell(
  quoteId: string,
  decision:
    | { outcome: "kept" }
    | { outcome: "upgraded"; plan_slug: string; option_slug: string },
): Promise<AcknowledgedSelection> {
  return fetchPublicAPI<AcknowledgedSelection>(
    `/quotes/${quoteId}/upsell-decision`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(decision),
    },
  );
}

/** The review's looks-good, recorded against the selection on review. */
export function confirmReview(quoteId: string): Promise<AcknowledgedSelection> {
  return fetchPublicAPI<AcknowledgedSelection>(
    `/quotes/${quoteId}/review-confirmation`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
}

/** The Cal host the call was booked with. Either field is null when Cal
 * did not say; the whole object is null (or absent on a server that
 * predates it) when no host was recorded. */
export interface ScheduledCallHost {
  name: string | null;
  username: string | null;
}

export interface ScheduledCall {
  /** UTC ISO start of the booked call. */
  start: string;
  host?: ScheduledCallHost | null;
}

export interface CallSlotDay {
  /** Calendar date in the requested timezone (`2026-09-01`). */
  date: string;
  /** UTC ISO starts of the bookable slots that day, ascending. */
  times: string[];
}

export interface CallSlots {
  timezone: string;
  days: CallSlotDay[];
}

/** The quote's upcoming booked call, or null. */
export function fetchScheduledCall(
  quoteId: string,
): Promise<{ call: ScheduledCall | null }> {
  return fetchPublicAPI<{ call: ScheduledCall | null }>(
    `/quotes/${quoteId}/call`,
  );
}

/** The times a call can be booked, grouped by day in the given timezone. */
export function fetchCallSlots(
  quoteId: string,
  timezone: string,
): Promise<CallSlots> {
  return fetchPublicAPI<CallSlots>(
    `/quotes/${quoteId}/call/slots?timezone=${encodeURIComponent(timezone)}`,
  );
}

export function scheduleCall(
  quoteId: string,
  booking: { start: string; timezone: string; email?: string },
): Promise<{ call: ScheduledCall }> {
  return fetchPublicAPI<{ call: ScheduledCall }>(`/quotes/${quoteId}/call`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(booking),
  });
}

/**
 * The application form through the quote credential (the `quote_session`
 * cookie). Failures surface as NetworkError straight from fetchPublicAPI,
 * which is the whole error contract FormV2 needs — 404 drives its
 * vanished-response reload, a 409's body carries `resolved` for the
 * reconcile path.
 */
export function quoteFormClient(args: {
  quoteId: string;
  formRef: string;
}): FormV2Client {
  const base = `/quotes/${args.quoteId}/application`;
  return {
    resume() {
      return fetchPublicAPI<V2ResumeResponse>(`${base}/resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_ref: args.formRef }),
      });
    },
    fetchResolvedView(responseId) {
      return fetchPublicAPI<V2ResolvedView>(
        `${base}/responses/${responseId}/resolved`,
      );
    },
    saveAnswer(responseId, fieldRef, value, formFieldVariantId) {
      return fetchPublicAPI<V2SavedAnswer>(
        `${base}/responses/${responseId}/answers/${fieldRef}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            value,
            form_field_variant_id: formFieldVariantId,
          }),
        },
      );
    },
    complete(responseId) {
      return fetchPublicAPI(`${base}/responses/${responseId}/complete`, {
        method: "POST",
      });
    },
  };
}
